import React from 'react';
import {
    ColumnPickerModal,
    PickableItem,
} from '@instinet-analytics/semantic-ui-components';
import { useDispatchUpdateVisibleColumns } from 'widgets/store/grid/visibleColumns/dispatcher';
import {
    getAdditionalOutlierColumns,
    OutliersColumn,
} from 'widgets/outliers/components/grid/columns';
import { initialOutliersConfigState } from 'widgets/outliers/store/config/OutliersConfigState';
import {
    applyCurrency,
    useColumnPortfolio,
} from 'widgets/blotter/columns/ColumnPortfolio';
import { useOutliersConfigSlice } from 'widgets/outliers/store/config/configSlice';
import { useSelectIsFeatureEnabled } from 'app/store/featureToggles/selectors';
import { WellKnownFeatureToggleKeys } from 'app/store/featureToggles/FeatureTogglesState';
import {
    applyCurrencyToColumn,
    ColumnDefinition,
} from '@instinet-analytics/semantic-ui-components';
import { useAppCurrency } from 'app/store/selectors';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { useWidgetEditedColumnNames } from 'grid/columnNames/hooks';

export default function OutliersColumnPicker() {
    const {
        config: { visibleColumns, plType },
    } = useOutliersConfigSlice();
    const columnPortfolio = useColumnPortfolio();
    const isLastPriceEnabled = useSelectIsFeatureEnabled(
        WellKnownFeatureToggleKeys.LastPriceEnabled
    );
    const currency = useAppCurrency();
    const { currencyCode } = useSelectAppSettings();
    const currencyPrefix = currency?.prefix ?? currencyCode;
    const { editedColumnNames } = useWidgetEditedColumnNames();

    const pnlDesc =
        plType === 'realised'
            ? 'Realized P&L'
            : plType === 'unrealised'
            ? 'Unrealized P&L'
            : 'Total P&L';

    const blotterPickableItems = Object.values(columnPortfolio).map(
        (column) => {
            const colWithCurrenciesApplied = applyCurrency(
                column,
                currencyPrefix
            );

            const haveEditedName =
                editedColumnNames[column.id] &&
                editedColumnNames[column.id].trim() !== column.name.trim();

            const ret = {
                value: colWithCurrenciesApplied.id,
                desc: colWithCurrenciesApplied.desc,
                name: haveEditedName
                    ? editedColumnNames[column.id]
                    : column.name,
                nameSuffix: haveEditedName ? column.name : undefined,
                tag: colWithCurrenciesApplied.tag,
            };

            return ret;
        }
    );

    const outliersPickableItems: PickableItem<OutliersColumn>[] = Object.values(
        getAdditionalOutlierColumns(isLastPriceEnabled)
    ).map((column) => {
        const col = applyCurrencyToColumn(
            column as ColumnDefinition,
            currencyPrefix
        );

        const haveEditedName =
            editedColumnNames[col.field] &&
            editedColumnNames[col.field].trim() !== col.name.trim();

        return {
            value: col.field as OutliersColumn,
            name: haveEditedName
                ? editedColumnNames[column.field]
                : col.name || col.field!,
            nameSuffix: haveEditedName ? col.name || col.field! : undefined,
            tag: col.tag,
            desc: col.field === 'pAndL' ? pnlDesc : col.desc,
        };
    });

    const allPickableItems = [
        ...blotterPickableItems,
        ...outliersPickableItems,
    ];

    const dispatchUpdateVisibleColumns = useDispatchUpdateVisibleColumns();

    const onUpdateVisibleColumns = (cols: OutliersColumn[]) => {
        dispatchUpdateVisibleColumns(cols);
    };

    return (
        <ColumnPickerModal
            selectedValues={visibleColumns}
            defaultSelectedValues={initialOutliersConfigState.visibleColumns}
            allItems={allPickableItems}
            onUpdateSelectedValues={onUpdateVisibleColumns}
        />
    );
}
